import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Settings, LogOut, Crown, Gift, CreditCard, Coins, ChevronDown, Sparkles, Zap, CalendarClock, Shield, Languages, LifeBuoy, Compass, FolderOpen } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { toast } from "sonner";
import { fmtCredits, fmtBytes } from "../lib/api";
import { useQuota } from "../context/QuotaContext";

const initials = (name, email) => {
  const s = (name || email || "U").trim();
  const parts = s.split(/\s+/);
  return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase() || s[0]?.toUpperCase() || "U";
};

const Avatar = ({ user, size = 32 }) => {
  const cls = "rounded-full object-cover shrink-0";
  const dim = { width: size, height: size };
  if (user?.picture) {
    return <img src={user.picture} alt={user?.name || "avatar"} referrerPolicy="no-referrer"
      className={cls} style={dim} onError={(e) => { e.currentTarget.style.display = "none"; }} />;
  }
  return (
    <span className="rounded-full bg-gradient-to-br from-blue-600 to-indigo-500 text-white font-bold flex items-center justify-center shrink-0"
      style={{ ...dim, fontSize: size * 0.4 }}>
      {initials(user?.name, user?.email)}
    </span>
  );
};

export default function ProfileMenu({ user, onConfig, onLogout, onTour }) {
  const { t, i18n } = useTranslation();
  const { quota, openUpgrade, openPortal, openTopup, cancelPendingChange } = useQuota();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const used = quota?.seconds_used || 0;
  const limit = quota?.seconds_limit || 1;
  const pct = Math.min(100, Math.round((used / limit) * 100));
  const low = (quota?.seconds_remaining || 0) < 60;
  const storageUsed = quota?.storage_used_bytes || 0;
  const storageLimit = quota?.storage_limit_bytes || 1;
  const storagePct = Math.min(100, Math.round((storageUsed / storageLimit) * 100));
  const storageLow = storagePct >= 90;
  const pending = quota?.pending_plan_change;
  const runAndClose = (fn) => () => { setOpen(false); fn && fn(); };
  const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString(i18n.language === "en" ? "en-US" : "es-ES", { day: "numeric", month: "short", year: "numeric" }); } catch { return ""; } };
  const doCancelPending = async (e) => {
    e.stopPropagation();
    setCancelling(true);
    try { await cancelPendingChange(); toast.success(t("menu.changeCancelled")); }
    catch { toast.error(t("menu.cancelError")); }
    setCancelling(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid="profile-menu-trigger"
          className="min-h-[44px] pl-1.5 pr-2.5 rounded-full flex items-center gap-2 border-2 border-slate-200 hover:bg-slate-100 transition-colors">
          <Avatar user={user} size={32} />
          {quota && (
            <span className="hidden sm:flex items-center gap-1.5 text-sm font-semibold text-slate-700">
              <Coins size={15} className={low ? "text-red-600" : "text-blue-600"} />
              <span data-testid="quota-minutes" className={low ? "text-red-600" : ""}>{fmtCredits(quota.seconds_remaining || 0)} {t("common.credits", { count: Math.round(quota.seconds_remaining || 0) })}</span>
            </span>
          )}
          <ChevronDown size={16} className="text-slate-400" />
        </button>
      </PopoverTrigger>

      <PopoverContent align="end" className="w-80 p-0 overflow-hidden" data-testid="profile-menu">
        {/* Cabecera usuario */}
        <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-3">
          <Avatar user={user} size={40} />
          <div className="min-w-0">
            <div className="font-bold text-slate-900 truncate">{user?.name || t("menu.myAccount")}</div>
            <div className="text-sm text-slate-500 truncate">{user?.email}</div>
          </div>
        </div>

        {/* Mi suscripción / Facturación */}
        {quota && (
          <div className="px-4 py-3 bg-slate-50 border-b border-slate-100" data-testid="subscription-card">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-slate-600 flex items-center gap-1.5"><CreditCard size={15} /> {t("menu.mySubscription")}</span>
              <span className="inline-flex items-center gap-1 text-xs font-bold text-blue-700 bg-blue-100 px-2 py-0.5 rounded-full">
                <Sparkles size={12} /> {quota.plan_label}
              </span>
            </div>
            <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
              <span>{t("menu.minutesUsed")}</span>
              <span className="font-semibold text-slate-700" data-testid="usage-label">{fmtCredits(limit)} / {fmtCredits(used)} {t("common.credits", { count: Math.round(limit) })}</span>
            </div>
            <div className="w-full h-2.5 bg-slate-200 rounded-full overflow-hidden">
              <div className={`h-full rounded-full transition-all ${low ? "bg-red-500" : pct > 80 ? "bg-amber-500" : "bg-blue-600"}`} style={{ width: `${pct}%` }} />
            </div>
            <div className="text-xs text-slate-400 mt-1">{t("menu.remainingPre")} <b className={low ? "text-red-600" : "text-slate-600"}>{fmtCredits(quota.seconds_remaining || 0)} {t("common.credits", { count: Math.round(quota.seconds_remaining || 0) })}</b></div>

            <div className="flex items-center justify-between text-xs text-slate-500 mt-3 mb-1">
              <span>{t("menu.storageUsed")}</span>
              <span className="font-semibold text-slate-700" data-testid="storage-usage-label">{fmtBytes(storageUsed)} / {fmtBytes(storageLimit)}</span>
            </div>
            <div className="w-full h-2.5 bg-slate-200 rounded-full overflow-hidden">
              <div className={`h-full rounded-full transition-all ${storageLow ? "bg-red-500" : storagePct > 80 ? "bg-amber-500" : "bg-blue-600"}`} style={{ width: `${storagePct}%` }} />
            </div>
            <button data-testid="menu-open-library" onClick={runAndClose(() => navigate("/app/library"))}
              className="mt-1.5 text-xs font-bold text-blue-700 hover:text-blue-900 flex items-center gap-1">
              <FolderOpen size={13} /> {t("menu.openLibrary")}
            </button>

            <div className="flex gap-2 mt-3">
              <button data-testid="menu-upgrade-plan" onClick={runAndClose(() => openUpgrade())}
                className="flex-1 min-h-[40px] rounded-lg bg-blue-600 text-white text-sm font-bold flex items-center justify-center gap-1.5 hover:bg-blue-700 transition-colors">
                <Crown size={15} /> {t("menu.upgradePlan")}
              </button>
              {quota.has_subscription && (
                <button data-testid="menu-manage-subscription" onClick={runAndClose(openPortal)}
                  className="flex-1 min-h-[40px] rounded-lg border-2 border-slate-200 text-slate-700 text-sm font-bold flex items-center justify-center gap-1.5 hover:bg-white transition-colors">
                  <CreditCard size={15} /> {t("menu.billing")}
                </button>
              )}
            </div>
            {quota.plan !== "gratis" && (
              <button data-testid="menu-topup" onClick={runAndClose(() => openTopup())}
                className="w-full mt-2 min-h-[40px] rounded-lg border-2 border-blue-200 bg-blue-50 text-blue-700 text-sm font-bold flex items-center justify-center gap-1.5 hover:bg-blue-100 transition-colors">
                <Zap size={15} /> {t("menu.topup")}
              </button>
            )}
            {pending && pending.plan && (
              <div data-testid="menu-pending-change" className="mt-2 text-xs text-slate-600 bg-white border border-blue-200 rounded-lg px-3 py-2">
                <div className="flex items-center gap-1.5 font-semibold text-blue-700"><CalendarClock size={13} /> {t("menu.scheduledChange")}</div>
                <div className="mt-0.5"><Trans i18nKey="menu.willChangeTo" values={{ plan: pending.plan_label, date: fmtDate(pending.effective_at) }} components={{ b: <b /> }} /></div>
                <button data-testid="menu-cancel-pending" onClick={doCancelPending} disabled={cancelling}
                  className="mt-1.5 text-xs font-bold text-red-600 hover:text-red-700 underline disabled:opacity-50">
                  {cancelling ? t("menu.cancelling") : t("menu.cancelPending")}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Acciones */}
        <div className="py-1">
          {/* Idioma */}
          <div className="px-4 py-2.5 border-b border-slate-100" data-testid="menu-language">
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-600 mb-2">
              <Languages size={18} className="text-slate-400" /> {t("menu.language")}
            </div>
            <div className="grid grid-cols-2 gap-2">
              {[{ id: "es", label: "Español", flag: "https://flagcdn.com/es.svg" }, { id: "en", label: "English", flag: "https://flagcdn.com/gb.svg" }].map((l) => {
                const active = (i18n.resolvedLanguage || i18n.language || "es").slice(0, 2) === l.id;
                return (
                  <button key={l.id} data-testid={`menu-lang-${l.id}`} type="button" onClick={() => i18n.changeLanguage(l.id)}
                    className={`flex items-center justify-center gap-2 border-2 rounded-lg px-3 py-2 text-sm font-semibold transition-colors ${active ? "border-blue-600 bg-blue-50 text-blue-700" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
                    <img src={l.flag} alt="" className="w-5 h-auto rounded-sm shadow-sm" /> {l.label}
                  </button>
                );
              })}
            </div>
          </div>
          {user?.role === "admin" && (
            <button data-testid="menu-admin" onClick={runAndClose(() => window.open("/admin", "_blank", "noopener"))}
              className="w-full px-4 py-2.5 flex items-center gap-3 text-violet-700 font-medium hover:bg-violet-50 transition-colors">
              <Shield size={18} className="text-violet-400" /> {t("menu.adminZone")}
            </button>
          )}
          {/* Encima de Configuración a propósito: es lo que queremos que la gente encuentre,
              y "Configuración" es donde nadie mira salvo que busque algo concreto. */}
          <button data-testid="menu-referidos" onClick={runAndClose(() => navigate("/app/referidos"))}
            className="w-full px-4 py-2.5 flex items-center gap-3 text-slate-700 font-medium hover:bg-slate-50 transition-colors">
            <Gift size={18} className="text-emerald-500" /> {t("menu.referrals")}
          </button>
          <button data-testid="menu-config" onClick={runAndClose(onConfig)}
            className="w-full px-4 py-2.5 flex items-center gap-3 text-slate-700 font-medium hover:bg-slate-50 transition-colors">
            <Settings size={18} className="text-slate-400" /> {t("menu.settings")}
          </button>
          <button data-testid="menu-help" onClick={runAndClose(() => (window.location.href = "/ayuda"))}
            className="w-full px-4 py-2.5 flex items-center gap-3 text-slate-700 font-medium hover:bg-slate-50 transition-colors">
            <LifeBuoy size={18} className="text-slate-400" /> {t("menu.help")}
          </button>
          {onTour && (
            <button data-testid="menu-tour" onClick={runAndClose(onTour)}
              className="w-full px-4 py-2.5 flex items-center gap-3 text-slate-700 font-medium hover:bg-slate-50 transition-colors">
              <Compass size={18} className="text-slate-400" /> {t("menu.tour")}
            </button>
          )}
          <button data-testid="menu-logout" onClick={runAndClose(onLogout)}
            className="w-full px-4 py-2.5 flex items-center gap-3 text-red-600 font-medium hover:bg-red-50 transition-colors">
            <LogOut size={18} /> {t("menu.logout")}
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
